import React, { useState, useEffect } from 'react';
import PageHeader from '../common/PageHeader/PageHeader';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';
import Button from '../common/Button/Button';
import { apiClient } from '../../services/apiClient';
import './AgentsManagement.css';

const STORAGE_KEY_LLM_CONFIG = 'llm-config';

const EMAIL_AGENT_ID = 'email-context';

const DEFAULT_OLLAMA_MODELS = [
  { value: 'gemma3:1b', label: 'gemma3:1b' },
  { value: 'gemma:2b', label: 'gemma:2b' },
  { value: 'llama2', label: 'llama2' },
  { value: 'mistral', label: 'mistral' }
];

const BASE_LLM_PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI',
    models: [
      { value: 'gpt-5.2-pro', label: 'GPT-5.2 Pro' },
      { value: 'gpt-5.2', label: 'GPT-5.2' },
      { value: 'gpt-5.1', label: 'GPT-5.1' },
      { value: 'gpt-5', label: 'GPT-5' },
      { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
      { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
    ]
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    models: [
      { value: 'claude-opus-4-5', label: 'Claude Opus 4.5' },
      { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
      { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
      { value: 'claude-opus-4-1', label: 'Claude Opus 4.1' },
      { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
      { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
      { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
      { value: 'claude-3-5-haiku-20241022', label: 'Claude 3 Haiku' }
    ]
  }
];

const getLlmProviders = () => {
  let onPremiseProviders = [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LLM_CONFIG);
    if (stored) {
      const parsed = JSON.parse(stored);
      const ollamaConfig = parsed?.onPremiseModels?.ollama;
      if (ollamaConfig?.enabled && ollamaConfig.model) {
        const modelSet = new Set([ollamaConfig.model, ...DEFAULT_OLLAMA_MODELS.map(m => m.value)]);
        const models = Array.from(modelSet).map(value => ({ value, label: value }));
        onPremiseProviders.push({
          id: 'ollama',
          name: 'Ollama (On-Premise)',
          models
        });
      }
    }
  } catch { /* fallthrough */ }
  return [...BASE_LLM_PROVIDERS, ...onPremiseProviders];
};

const normalizeEmailAgentConfig = (config = {}) => ({
  email_account: config.email_account || '',
  crewai_integration_token: config.crewai_integration_token || '',
  provider: config.provider || 'openai',
  model: config.model || 'gpt-4o',
  api_key: config.api_key || config.apiKey || ''
});

const DEFAULT_AGENTS = [
  {
    id: 'weather-forecast',
    name: 'Weather Forecast',
    description: "Checks the user's location and retrieves a 48-hour weather forecast, presenting it directly in the conversation.",
    icon: 'fas fa-cloud-sun',
    color: '#38bdf8',
    provider: 'n8n',
    configFields: [
      { key: 'n8n_webhook_url', label: 'n8n Webhook URL', placeholder: 'Leave empty for Docker-internal default' }
    ]
  },
  {
    id: 'email-context',
    name: 'Email Context Agent',
    description: 'Searches connected email to find relevant messages and summarizes them for added context.',
    icon: 'fas fa-envelope-open-text',
    color: '#a78bfa',
    provider: 'crewai',
    triggerType: 'per-prompt',
    status: 'available',
    configFields: [
      { key: 'email_account', label: 'Email Account', placeholder: 'e.g. user@company.com' },
      { key: 'crewai_integration_token', label: 'CrewAI Integration Token', placeholder: 'Required (stored locally)' },
      { key: 'provider', label: 'LLM Provider', placeholder: 'e.g. openai, ollama' },
      { key: 'model', label: 'Model', placeholder: 'e.g. gpt-4o-mini, llama3' },
      { key: 'api_key', label: 'API Key (if required)', placeholder: 'Leave empty to use server defaults' }
    ],
    configHint: 'CrewAI token is required before setting provider or model overrides.'
  },
  {
    id: 'pricing-sync',
    name: 'LLM Pricing Sync',
    description: 'Scrapes provider pricing pages and updates model cost references.',
    icon: 'fas fa-tags',
    color: '#f59e0b',
    provider: 'internal'
  }
];

const buildDefaultAgents = () =>
  DEFAULT_AGENTS.map((def) => {
    const baseConfig = Object.fromEntries((def.configFields || []).map((f) => [f.key, '']));
    const config = def.id === EMAIL_AGENT_ID
      ? { ...baseConfig, ...normalizeEmailAgentConfig(baseConfig) }
      : baseConfig;
    return {
      ...def,
      enabled: false,
      config
    };
  });

export default function AgentsManagement() {
  const [agenticMode, setAgenticMode] = useState(false);
  const [agents, setAgents] = useState(buildDefaultAgents);
  const [emailAgentAccount, setEmailAgentAccount] = useState('');
  const [crewaiIntegrationToken, setCrewaiIntegrationToken] = useState('');

  const [expandedAgent, setExpandedAgent] = useState(null);
  const [syncingPricing, setSyncingPricing] = useState(false);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('agenticModeChanged', { detail: { enabled: agenticMode } }));
  }, [agenticMode]);

  useEffect(() => {
    const loadConfigFromBackend = async () => {
      try {
        const config = await apiClient.getAgentConfig();
        if (!config) return;

        const backendAgents = config.agents || {};
        const emailDefaults = {
          email_account: config.email_account || '',
          crewai_integration_token: config.crewai_integration_token || ''
        };
        const hydratedAgents = DEFAULT_AGENTS.map((def) => ({
          ...def,
          enabled: backendAgents[def.id]?.enabled ?? false,
          lastRun: backendAgents[def.id]?.lastRun,
          config: (() => {
            const baseConfig = Object.fromEntries((def.configFields || []).map((f) => [f.key, '']));
            const mergedConfig = def.id === EMAIL_AGENT_ID
              ? { ...baseConfig, ...emailDefaults, ...(backendAgents[def.id]?.config || {}) }
              : { ...baseConfig, ...(backendAgents[def.id]?.config || {}) };
            return def.id === EMAIL_AGENT_ID
              ? { ...mergedConfig, ...normalizeEmailAgentConfig(mergedConfig) }
              : mergedConfig;
          })()
        }));

        setAgenticMode(Boolean(config.agenticMode));
        setEmailAgentAccount(config.email_account || '');
        setCrewaiIntegrationToken(config.crewai_integration_token || '');      
        setAgents(hydratedAgents);
      } catch (error) {
        console.error('Failed to load agent config from backend:', error);
      }
    };

    loadConfigFromBackend();
  }, []);

  const saveConfigToBackend = async (newMode, newAgents) => {
    const emailAgentConfig = newAgents.find((a) => a.id === EMAIL_AGENT_ID)?.config || {};
    const resolvedEmailAccount = emailAgentAccount || emailAgentConfig.email_account || '';
    const resolvedCrewaiToken = crewaiIntegrationToken || emailAgentConfig.crewai_integration_token || '';
    const payload = {
      agenticMode: newMode,
      agents: Object.fromEntries(
        newAgents.map((a) => [
          a.id,
          {
            enabled: Boolean(a.enabled),
            config: a.id === EMAIL_AGENT_ID ? normalizeEmailAgentConfig(a.config || {}) : (a.config || {}),
            ...(a.lastRun ? { lastRun: a.lastRun } : {})
          }
        ])
      ),
      email_account: resolvedEmailAccount,
      crewai_integration_token: resolvedCrewaiToken
    };

    await apiClient.saveAgentConfig(payload);
  };

  const handleModeToggle = async (newMode) => {
    setAgenticMode(newMode);
    try {
      await saveConfigToBackend(newMode, agents);
    } catch (error) {
      console.error('Failed to save agentic mode:', error);
    }
  };

  const toggleAgent = async (agentId) => {
    const newAgents = agents.map((a) =>
      a.id === agentId ? { ...a, enabled: !a.enabled } : a
    );
    setAgents(newAgents);
    try {
      await saveConfigToBackend(agenticMode, newAgents);
    } catch (error) {
      console.error('Failed to save agent config:', error);
    }
  };

  const updateAgentConfig = async (agentId, key, value) => {
    if (agentId === EMAIL_AGENT_ID && key === 'email_account') {
      setEmailAgentAccount(value);
    }
    if (agentId === EMAIL_AGENT_ID && key === 'crewai_integration_token') {
      setCrewaiIntegrationToken(value);
    }
    const newAgents = agents.map((a) =>
      a.id === agentId ? { ...a, config: { ...a.config, [key]: value } } : a
    );
    setAgents(newAgents);
    try {
      await saveConfigToBackend(agenticMode, newAgents);
    } catch (error) {
      console.error('Failed to save agent config field:', error);
    }
  };

  const runPricingSyncAgent = async () => {
    setSyncingPricing(true);
    try {
      await apiClient.triggerPricingSync();
    } catch (error) {
      console.error('Failed to trigger pricing sync:', error);
    } finally {
      setSyncingPricing(false);
    }
  };

  const updateEmailAgentProvider = async (agentId, providerId) => {
    const llmProviders = getLlmProviders();
    const selectedProvider = llmProviders.find(p => p.id === providerId);
    const availableModels = selectedProvider?.models || [];
    const defaultModel = availableModels[0]?.value || '';
    const newAgents = agents.map(a => {
      if (a.id !== agentId) {
        return a;
      }
      const nextConfig = { ...a.config, provider: providerId };
      if (!providerId) {
        nextConfig.model = '';
        nextConfig.api_key = '';
      } else if (!availableModels.some(m => m.value === a.config?.model)) {
        nextConfig.model = defaultModel;
      }
      if (providerId === 'ollama') {
        nextConfig.api_key = '';
      }
      return { ...a, config: nextConfig };
    });
    setAgents(newAgents);
    try {
      await saveConfigToBackend(agenticMode, newAgents);
    } catch (error) {
      console.error('Failed to save email agent provider:', error);
    }
  };

  return (
    <div className="page-container agents-management">
      <div className="page-content">
      <PageHeader
        title="Agents"
        description="Configure agents that run alongside your prompts to enrich responses with real-time data."
        icon="fas fa-robot"
        accentColor="purple"
      />

      <div className="settings-list">
        {/* Master toggle */}
        <div className="setting-card">
          <div className="setting-row">
            <div className="setting-info">
              <span className="setting-label">Agentic Mode</span>
              <p className="setting-desc">
                When enabled, active agents are triggered on every prompt to enrich responses with real-time data. Individual agents can be toggled independently below.
              </p>
            </div>
            <ToggleSwitch checked={agenticMode} onChange={handleModeToggle} />
          </div>
        </div>

        {/* Agent cards */}
        {agents.map(agent => (
          <div
            key={agent.id}
            className={`setting-card agent-card${!agenticMode && agent.id !== 'pricing-sync' ? ' agent-card--off' : ''}`}
          >
            <div className="setting-row">
              <div className="agent-card-left">
                <div className="agent-icon" style={{ '--agent-color': agent.color }}>
                  <i className={agent.icon}></i>
                </div>
                <div className="setting-info">
                  <span className="setting-label">
                    {agent.name}
                    <span className="agent-badge">{agent.provider}</span>
                  </span>
                  {agent.description && <p className="setting-desc">{agent.description}</p>}

                  {agent.id === 'pricing-sync' && agent.lastRun && (
                    <div className="agent-metadata">
                      <i className="far fa-clock"></i>
                      Last synced {new Date(agent.lastRun).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="agent-card-controls">
                {agent.id === 'pricing-sync' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={syncingPricing ? 'fas fa-spinner fa-spin' : 'fas fa-play'}
                    onClick={runPricingSyncAgent}
                    disabled={syncingPricing}
                    className="agent-run-btn"
                  >
                    {syncingPricing ? 'Running...' : 'Run now'}
                  </Button>
                ) : (
                  <>
                    {agent.configFields && agent.configFields.length > 0 && (
                      <button
                        className="agent-config-btn"
                        onClick={() =>
                          setExpandedAgent(expandedAgent === agent.id ? null : agent.id)
                        }
                        disabled={!agenticMode}
                      >
                        <i className={`fas fa-chevron-${expandedAgent === agent.id ? 'up' : 'down'}`}></i>
                      </button>
                    )}

                    <ToggleSwitch
                      checked={agent.enabled}
                      onChange={() => toggleAgent(agent.id)}
                      disabled={!agenticMode}
                      size="sm"
                    />
                  </>
                )}
              </div>
            </div>

            {expandedAgent === agent.id && agent.configFields && (
              <div className="agent-config-panel">
                {agent.configFields.map(field => {
                  const isEmailAgent = agent.id === EMAIL_AGENT_ID;
                  const llmProviders = isEmailAgent ? getLlmProviders() : [];
                  const tokenValue = (agent.config?.crewai_integration_token || '').trim();
                  const hasToken = Boolean(tokenValue);
                  const isTokenField = field.key === 'crewai_integration_token';
                  const isEmailAccountField = field.key === 'email_account';
                  const shouldHideField = isEmailAgent && !hasToken && !isTokenField && !isEmailAccountField;
                  if (shouldHideField) {
                    return null;
                  }
                  if (isEmailAgent && field.key === 'provider') {
                    return (
                      <div key={field.key} className="agent-config-field">
                        <label>{field.label}</label>
                        <select
                          value={agent.config?.provider || ''}
                          onChange={(e) => updateEmailAgentProvider(agent.id, e.target.value)}
                        >
                          <option value="">Select a provider</option>
                          {llmProviders.map(provider => (
                            <option key={provider.id} value={provider.id}>
                              {provider.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  if (isEmailAgent && field.key === 'model') {
                    const selectedProvider = llmProviders.find(p => p.id === agent.config?.provider);
                    const models = selectedProvider?.models || [];
                    return (
                      <div key={field.key} className="agent-config-field">
                        <label>{field.label}</label>
                        <select
                          value={agent.config?.model || ''}
                          onChange={(e) => updateAgentConfig(agent.id, field.key, e.target.value)}
                          disabled={!agent.config?.provider || models.length === 0}
                        >
                          <option value="">Select a model</option>
                          {models.map(model => (
                            <option key={model.value} value={model.value}>
                              {model.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  if (isEmailAgent && field.key === 'api_key') {
                    const isOllama = agent.config?.provider === 'ollama';
                    if (isOllama) {
                      return null;
                    }
                  }
                  return (
                    <div key={field.key} className="agent-config-field">
                      <label>{field.label}</label>
                      <input
                        type={isTokenField || field.key === 'api_key' ? 'password' : 'text'}
                        placeholder={field.placeholder}
                        value={agent.config?.[field.key] || ''}
                        onChange={(e) => updateAgentConfig(agent.id, field.key, e.target.value)}
                      />
                    </div>
                  );
                })}
                {agent.configHint && (
                  <p className="agent-config-hint">
                    <i className="fas fa-info-circle"></i>
                    {agent.configHint}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
